import { FC, memo } from 'react'
import { Box, useToken, useColorModeValue } from '@chakra-ui/react'

type Props = {
  size?: string | number
}

const WithoutPermissionIllustration: FC<Props> = ({ size = '150px' }) => {
  const [primary500, primary700, gray50, gray900, red100, red500] = useToken('colors', [
    'primary.500',
    'primary.500',
    'gray.50',
    'gray.900',
    'red.100',
    'red.500',
  ])

  const bgColor = useColorModeValue(gray50, gray900)
  const strokeColor = useColorModeValue(primary500, primary700)
  const circleStrokenWidth = '18px'
  const strokenWidth = '6px'
  const xCircleBg = useColorModeValue(red100, red100)
  const xCircleBorder = useColorModeValue(red500, red500)

  return (
    <Box w={size} h={size} pos="relative">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 569.9 468.81">
        <g id="BG">
          <path
            fill={bgColor}
            d="M646.9,533.26l-32.52-22.77-4.77-3.34A232.19,232.19,0,0,0,627.5,417.5c0-128.68-104.32-233-233-233a232,232,0,0,0-107.1,26c-12.44-8.81-24.7-18.21-37.6-26.3-.19-.12-.37-.27-.56-.4-6.64-4.65-16.67-1.2-20.52,5.38-4.38,7.49-1.28,15.87,5.38,20.53l25.44,17.81a233.92,233.92,0,0,0-22.21,18l-27.61-19.34c-13.25-9.27-26.22-19.35-39.92-27.94-.19-.12-.37-.27-.56-.4-6.64-4.65-16.67-1.2-20.52,5.38-4.38,7.49-1.28,15.87,5.38,20.53l32.67,22.88,29.6,20.72a234.18,234.18,0,0,0-16.44,22c-10.17-7.34-20.29-14.91-30.88-21.52l-.54-.39c-6.64-4.65-16.67-1.2-20.52,5.39-4.38,7.48-1.28,15.86,5.38,20.52l31.45,22,.18.13a231.9,231.9,0,0,0-23.5,102c0,128.68,104.32,233,233,233a232.27,232.27,0,0,0,166.76-70.3l18.18,12.74c13.19,9.23,26.11,19.28,39.75,27.83.2.12.38.27.57.4,6.64,4.65,16.67,1.2,20.52-5.38,4.38-7.49,1.28-15.87-5.38-20.53l-32.52-22.77L580.83,557.4a234.37,234.37,0,0,0,15.33-23.12c11.57,8.27,23,17,35,24.49.2.12.38.27.57.4,6.64,4.65,16.67,1.2,20.52-5.38C656.66,546.3,653.56,537.92,646.9,533.26Z"
            transform="translate(-97.05 -181.69)"
          />
          <path
            fill={bgColor}
            d="M136.22,247.12l-16.71-11.7c-6.45-4.51-17-1.38-20.52,5.39-4.06,7.68-1.5,15.7,5.38,20.52L121.08,273c6.44,4.51,17,1.38,20.52-5.38C145.66,260,143.1,251.94,136.22,247.12Z"
            transform="translate(-97.05 -181.69)"
          />
        </g>
        <g id="Stars">
          <g id="Star">
            <path
              fill={strokeColor}
              d="M274.5,561h20a1.5,1.5,0,0,0,0-3h-20a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M283,549.5v20a1.5,1.5,0,0,0,3,0v-20a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-2" data-name="Star">
            <path
              fill={strokeColor}
              d="M566.5,341h16a1.5,1.5,0,0,0,0-3h-16a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M573,331.5v16a1.5,1.5,0,0,0,3,0v-16a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-3" data-name="Star">
            <path
              fill={strokeColor}
              d="M408.5,211h12a1.5,1.5,0,0,0,0-3h-12a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M413,203.5v12a1.5,1.5,0,0,0,3,0v-12a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-4" data-name="Star">
            <path
              fill={strokeColor}
              d="M559.5,621h10a1.5,1.5,0,0,0,0-3h-10a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M563,614.5v10a1.5,1.5,0,0,0,3,0v-10a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-5" data-name="Star">
            <path
              fill={strokeColor}
              d="M369.5,611h10a1.5,1.5,0,0,0,0-3h-10a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M373,604.5v10a1.5,1.5,0,0,0,3,0v-10a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-6" data-name="Star">
            <path
              fill={strokeColor}
              d="M249.5,451h10a1.5,1.5,0,0,0,0-3h-10a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M253,444.5v10a1.5,1.5,0,0,0,3,0v-10a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-7" data-name="Star">
            <path
              fill={strokeColor}
              d="M643.5,441h22a1.5,1.5,0,0,0,0-3h-22a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M653,428.5v22a1.5,1.5,0,0,0,3,0v-22a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-8" data-name="Star">
            <path
              fill={strokeColor}
              d="M135.5,451h18a1.5,1.5,0,0,0,0-3h-18a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M143,440.5v18a1.5,1.5,0,0,0,3,0v-18a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-9" data-name="Star">
            <path
              fill={strokeColor}
              d="M564.5,221h20a1.5,1.5,0,0,0,0-3h-20a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M573,209.5v20a1.5,1.5,0,0,0,3,0v-20a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-10" data-name="Star">
            <path
              fill={strokeColor}
              d="M262.5,271h24a1.5,1.5,0,0,0,0-3h-24a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M273,257.5v24a1.5,1.5,0,0,0,3,0v-24a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
        </g>
        <g>
          <g>
            <circle
              className="cls-3"
              cx="301.08"
              cy="183.11"
              r="88.81"
              fill={bgColor}
              stroke={strokeColor}
              strokeWidth={circleStrokenWidth}
            />
            <path
              d="M332.58,521a73.4,73.4,0,0,1,3-8.2l-.91,2.15a141.83,141.83,0,0,1,12.84-23.64c1.88-2.81,3.86-5.57,5.93-8.25l-1.41,1.82A150.7,150.7,0,0,1,378,458.65c1.69-1.33,2.64-4.33,2.64-6.36a9.15,9.15,0,0,0-2.64-6.37,9.05,9.05,0,0,0-6.36-2.63l-2.39.32a9.1,9.1,0,0,0-4,2.31,157.59,157.59,0,0,0-33.7,36.94A146.9,146.9,0,0,0,317,511.17c-.64,1.64-1.26,3.3-1.75,5-1.29,4.52,1.58,10,6.29,11.07A9.17,9.17,0,0,0,332.58,521Z"
              transform="translate(-97.05 -181.69)"
              fill={strokeColor}
              stroke={strokeColor}
              strokeWidth={strokenWidth}
            />
            <path
              d="M481,518.82a125.44,125.44,0,0,0-7.58-17.3,179.1,179.1,0,0,0-23.82-35.19,153.83,153.83,0,0,0-21.28-20.41c-2-1.54-3.77-2.63-6.36-2.63a9.19,9.19,0,0,0-6.37,2.63,9.07,9.07,0,0,0-2.63,6.37c.1,2.22.8,4.92,2.63,6.36a157.16,157.16,0,0,1,26.79,27.15L441,484A169.68,169.68,0,0,1,460,514.75q1.37,2.85,2.59,5.76l-.91-2.15c.73,1.73,1.45,3.47,2.05,5.24a9.47,9.47,0,0,0,4.13,5.38,9.19,9.19,0,0,0,6.94.91c4.43-1.43,7.88-6.29,6.28-11.07Z"
              transform="translate(-97.05 -181.69)"
              fill={strokeColor}
              stroke={strokeColor}
              strokeWidth={strokenWidth}
            />
            <path
              d="M377.79,418.6a16,16,0,0,1,.46-2.05c.2-.75.44-1.49.7-2.22.16-.44.34-.87.51-1.31.23-.61-.75,1.69-.28.66.12-.27.24-.54.37-.8a30.34,30.34,0,0,1,1.72-3.11c.32-.49.66-1,1-1.45.73-1-.85,1.05-.28.36.25-.31.51-.62.77-.92a29.12,29.12,0,0,1,3-2.87c.52-.45-1.2.88-.64.49l.5-.36c.27-.19.54-.38.82-.56a21.09,21.09,0,0,1,2.12-1.21l1.09-.52c.82-.38-1.48.58-.17.09a24.14,24.14,0,0,1,2.62-.83c.84-.22,1.69-.37,2.55-.53s-.59.08-.64.08c.28,0,.55-.06.82-.09.57-.06,1.13-.1,1.7-.12,1-.05,2-.05,3,0,.51,0,1,.05,1.53.1l.82.08c1.05.1-1.2-.21-.47-.06a34.54,34.54,0,0,1,5.18,1.33,1.9,1.9,0,0,1,.64.25s-1.5-.68-.68-.29l1,.46a22,22,0,0,1,2.11,1.2c.32.2.62.42.93.63,1.11.76-.95-.79-.25-.21s1.19,1,1.75,1.55.84.86,1.24,1.31l.71.84c.47.57-1.08-1.46-.16-.19a29.27,29.27,0,0,1,1.92,3.07c.29.54.56,1.09.82,1.65s.66,1.69-.19-.48c.1.25.21.5.31.76.34.88.64,1.77.9,2.68a19.15,19.15,0,0,1,.58,2.6,6,6,0,0,0,2.76,3.58,6.18,6.18,0,0,0,4.63.61,6,6,0,0,0,3.58-2.76,7.1,7.1,0,0,0,.61-4.63,31.65,31.65,0,0,0-3.05-8.91,29.87,29.87,0,0,0-13.38-13.78,35.36,35.36,0,0,0-26.79-1.56,30.79,30.79,0,0,0-16.33,13.68,31.38,31.38,0,0,0-4,10.57,6.58,6.58,0,0,0,.61,4.63,6.15,6.15,0,0,0,3.58,2.76c2.86.65,6.88-.88,7.39-4.19Z"
              transform="translate(-97.05 -181.69)"
              fill={strokeColor}
              stroke={strokeColor}
              strokeWidth={strokenWidth}
            />
          </g>
          <g>
            <circle
              className="cls-4"
              cx="387.24"
              cy="327.59"
              r="47.72"
              fill={xCircleBg}
              stroke={xCircleBorder}
              strokeWidth={circleStrokenWidth}
            />
            <g>
              <path
                fill={xCircleBorder}
                stroke={xCircleBorder}
                strokeWidth={strokenWidth}
                d="M493.43,489.13l-9.34,11.14L469.18,518l-3.43,4.09c-2,2.4-2.54,6.16,0,8.49,2.22,2,6.33,2.56,8.48,0l9.35-11.14,14.91-17.77,3.43-4.09c2-2.4,2.53-6.16,0-8.49-2.23-2-6.33-2.56-8.49,0Z"
                transform="translate(-97.05 -181.69)"
              />
              <path
                fill={xCircleBorder}
                stroke={xCircleBorder}
                strokeWidth={strokenWidth}
                d="M465.75,497.62l9.34,11.14L490,526.53l3.43,4.09c2,2.41,6.43,2.24,8.49,0a6.17,6.17,0,0,0,0-8.49L492.57,511l-14.91-17.76-3.43-4.1c-2-2.41-6.42-2.24-8.48,0a6.15,6.15,0,0,0,0,8.49Z"
                transform="translate(-97.05 -181.69)"
              />
            </g>
          </g>
        </g>
      </svg>
    </Box>
  )
}

export default memo(WithoutPermissionIllustration)
